import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import CrewChangeForm from '../../components/HR/CrewChangeForm';
import { CrewChange, TableColumn } from '../../types';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCrewChanges, useCreateCrewChange, useUpdateCrewChange, useDeleteCrewChange } from '../../hooks/useCrewChanges';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';

const CrewChangeManagement: React.FC = () => {
  const queryOptions = useMemo(() => ({ sort: [{ field: 'createdAt', order: 'desc' as const }], pagination: { pageSize: 1000 } }), []);
  const { data: crewChanges = [], isLoading, error, refetch } = useCrewChanges(queryOptions);
  const { data: employees = [] } = useEmployees(queryOptions);
  const { data: branches = [] } = useBranches(queryOptions);
  const createMutation = useCreateCrewChange(() => refetch());
  const updateMutation = useUpdateCrewChange(() => refetch());
  const deleteMutation = useDeleteCrewChange(() => refetch());

  const [showForm, setShowForm] = useState(false);
  const [editingCrewChange, setEditingCrewChange] = useState<CrewChange | undefined>();
  // Optimistic overlay while a checkbox change is being saved
  const [editableChanges, setEditableChanges] = useState<Record<string, Partial<CrewChange>>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [raftFilter, setRaftFilter] = useState<string>('all');
  const [takenFilter, setTakenFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const getEmployeeName = useCallback((employeeId: string) => {
    const employee = employees.find((e: { id?: string; documentId?: string }) => e.id?.toString() === employeeId || e.documentId === employeeId);
    return employee ? (employee as { name: string }).name : '-';
  }, [employees]);

  // branchId may be a documentId (new records) or a numeric id (old records)
  const getBranchName = useCallback((branchId: string) => {
    const branch: any = branches.find(
      (b: any) => b.documentId === branchId || String(b.id) === String(branchId)
    );
    return branch?.name ?? '-';
  }, [branches]);

  const filteredCrewChanges = useMemo(() => {
    return crewChanges.filter(change => {
      const employeeName = getEmployeeName(change.employeeId!);
      const branchName = getBranchName(change.branchId!);
      const matchesSearch = !searchTerm || (
        employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (change.notes && change.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        change.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || change.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || String(change.branchId) === String(branchFilter);
      const matchesRaft = raftFilter === 'all' || (raftFilter === 'taken' && change.raftTaken) || (raftFilter === 'not_taken' && !change.raftTaken);
      const matchesTaken = takenFilter === 'all' || (takenFilter === 'taken' && change.takenTaken) || (takenFilter === 'not_taken' && !change.takenTaken);
      const changeDate = new Date(change.changeDate);
      const matchesDateFrom = !dateFromFilter || changeDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || changeDate <= new Date(dateToFilter);
      return matchesSearch && matchesStatus && matchesBranch && matchesRaft && matchesTaken && matchesDateFrom && matchesDateTo;
    });
  }, [crewChanges, searchTerm, statusFilter, branchFilter, raftFilter, takenFilter, dateFromFilter, dateToFilter, getEmployeeName, getBranchName]);

  const getCurrentValue = useCallback((change: CrewChange, field: 'raftTaken' | 'takenTaken'): boolean => {
    const edits = editableChanges[change.id];
    return edits && edits[field] !== undefined ? edits[field]! : change[field];
  }, [editableChanges]);

  // Checkbox changes save immediately — no separate "Save Changes" step.
  // Sign On and Sign Off are mutually exclusive: checking one unchecks the other.
  const handleInlineEdit = useCallback(async (item: CrewChange, field: 'raftTaken' | 'takenTaken', value: boolean) => {
    const other = field === 'raftTaken' ? 'takenTaken' : 'raftTaken';
    const updates: Partial<CrewChange> = { [field]: value };
    if (value && getCurrentValue(item, other)) updates[other] = false;
    setEditableChanges(prev => ({ ...prev, [item.id]: { ...prev[item.id], ...updates } }));
    try {
      await updateMutation.execute({ id: item.id, data: { ...updates, documentId: item.documentId } });
    } finally {
      setEditableChanges(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCurrentValue]);

  const filters: FilterConfig[] = useMemo(() => [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All Statuses' }, { value: 'sign_on', label: 'Sign On' }, { value: 'sign_off', label: 'Sign Off' }, { value: 'continue', label: 'Continue' }] },
    { key: 'branch', label: 'Vessel', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [{ value: 'all', label: 'All Vessels' }, ...branches.filter((b: any) => b.type === 'vessel').map((b: any) => ({ value: b.documentId || String(b.id), label: b.name }))] },
    { key: 'raft', label: 'Immigration Sign Off', type: 'select', value: raftFilter, onChange: setRaftFilter, options: [{ value: 'all', label: 'All' }, { value: 'taken', label: 'Taken' }, { value: 'not_taken', label: 'Not Taken' }] },
    { key: 'taken', label: 'Immigration Sign On', type: 'select', value: takenFilter, onChange: setTakenFilter, options: [{ value: 'all', label: 'All' }, { value: 'taken', label: 'Taken' }, { value: 'not_taken', label: 'Not Taken' }] },
    { key: 'dateFrom', label: 'From Date', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'To Date', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ], [statusFilter, branchFilter, raftFilter, takenFilter, dateFromFilter, dateToFilter, branches]);

  const handleExport = () => {
    const exportData = filteredCrewChanges.map(change => ({
      'Employee': getEmployeeName(change.employeeId!), 'Status': change.status.replace('_', ' ').toUpperCase(), 'Change Date': change.changeDate,
      'Vessel/Headquarters': getBranchName(change.branchId!), 'Immigration Sign Off': change.raftTaken ? 'Yes' : 'No', 'Immigration Sign On': change.takenTaken ? 'Yes' : 'No',
      'Notes': change.notes, 'Created Date': change.createdAt ? new Date(change.createdAt!).toLocaleDateString() : '-'
    }));
    exportToExcel(exportData, `crew_changes_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredCrewChanges.map(change => ({
      'Employee': getEmployeeName(change.employeeId!), 'Status': change.status.replace('_', ' ').toUpperCase(), 'Change Date': change.changeDate,
      'Vessel/Headquarters': getBranchName(change.branchId!), 'Immigration Sign Off': change.raftTaken ? 'Yes' : 'No', 'Immigration Sign On': change.takenTaken ? 'Yes' : 'No',
      'Notes': change.notes, 'Created Date': change.createdAt ? new Date(change.createdAt!).toLocaleDateString() : '-'
    }));
    printAsExcel(exportData, 'Crew Changes');
  };

  const handleEdit = (crewChange: CrewChange) => { setEditingCrewChange(crewChange); setShowForm(true); };
  const handleDelete = async (crewChange: CrewChange) => {
    const employeeName = getEmployeeName(crewChange.employeeId!);
    if (window.confirm(`Are you sure you want to delete crew change record for ${employeeName}?`)) {
      await deleteMutation.execute(crewChange.documentId || crewChange.id);
    }
  };
  const handleAdd = () => { setEditingCrewChange(undefined); setShowForm(true); };
  const handleFormSubmit = async (data: Partial<CrewChange>) => {
    // documentId is required so the update targets the right record in Strapi 5
    if (editingCrewChange) {
      await updateMutation.execute({
        id: editingCrewChange.id,
        data: { ...data, documentId: editingCrewChange.documentId },
      });
    } else {
      await createMutation.execute(data);
    }
    setShowForm(false);
    setEditingCrewChange(undefined);
  };
  const handleFormCancel = () => { setShowForm(false); setEditingCrewChange(undefined); };
  const onClearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setBranchFilter('all'); setRaftFilter('all'); setTakenFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  const columns: TableColumn[] = useMemo(() => [
    { key: 'employeeId', label: 'Employee', sortable: true, render: (_: string, item: CrewChange) => getEmployeeName(item.employeeId!) },
    { key: 'status', label: 'Status', sortable: true, render: (v: string) => {
      let colorClass = 'bg-blue-100 text-blue-800';
      if (v === 'sign_on') colorClass = 'bg-green-100 text-green-800';
      else if (v === 'sign_off') colorClass = 'bg-red-100 text-red-800';
      else if (v === 'continue') colorClass = 'bg-yellow-100 text-yellow-800';
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>{v.replace('_', ' ').toUpperCase()}</span>;
    }},
    { key: 'changeDate', label: 'Date', sortable: true, render: (v: string) => new Date(v).toLocaleDateString() },
    { key: 'branchId', label: 'Vessel/Headquarters', sortable: true, render: (_: string, item: CrewChange) => getBranchName(item.branchId!) },
    { key: 'raftTaken', label: 'Immigration Sign Off', sortable: true, render: (_: boolean, item: CrewChange) => (
      <div className="flex items-center">
        <input type="checkbox" checked={getCurrentValue(item, 'raftTaken')} onChange={(e) => handleInlineEdit(item, 'raftTaken', e.target.checked)}
          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${editableChanges[item.id]?.raftTaken !== undefined ? 'bg-yellow-50 border-yellow-300' : ''}`} />
        {editableChanges[item.id]?.raftTaken !== undefined && <span className="ml-1 text-xs text-yellow-600" title="Saving...">…</span>}
      </div>
    )},
    { key: 'takenTaken', label: 'Immigration Sign On', sortable: true, render: (_: boolean, item: CrewChange) => (
      <div className="flex items-center">
        <input type="checkbox" checked={getCurrentValue(item, 'takenTaken')} onChange={(e) => handleInlineEdit(item, 'takenTaken', e.target.checked)}
          className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${editableChanges[item.id]?.takenTaken !== undefined ? 'bg-yellow-50 border-yellow-300' : ''}`} />
        {editableChanges[item.id]?.takenTaken !== undefined && <span className="ml-1 text-xs text-yellow-600" title="Saving...">…</span>}
      </div>
    )},
    { key: 'notes', label: 'Notes', sortable: false },
    { key: 'createdAt', label: 'Created', sortable: true, render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' }
  ], [editableChanges, getEmployeeName, getBranchName, getCurrentValue, handleInlineEdit]);

  if (isLoading) return <div className="flex justify-center items-center h-64"><div className="text-gray-600">Loading crew changes...</div></div>;
  if (error) return <ErrorState message="Error loading crew changes" onRetry={refetch} entityName="crew changes" />;

  if (showForm) return (
    <div className="space-y-6">
      <CrewChangeForm crewChange={editingCrewChange} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Crew Change Management" description="Track crew sign on/off status, dates, vessels, and history" />
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0"><svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg></div>
          <div className="ml-3"><p className="text-sm text-yellow-700">Crew changes will automatically update the crew list. Sign On/Continue will add/update crew members, while Sign Off will remove them from active crew list and update history. Immigration checkboxes save instantly when clicked — unchecking "Immigration Sign On" removes the employee from the active crew list.</p></div>
        </div>
      </div>
      <FilterBar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search crew changes..." filters={filters} onClearFilters={onClearFilters} totalCount={crewChanges.length} filteredCount={filteredCrewChanges.length} entityName="crew changes" onAdd={handleAdd} addLabel="Add Crew Change" onExport={handleExport} onPrint={handlePrint} />
      <div className="card"><Table columns={columns} data={filteredCrewChanges} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} /></div>
    </div>
  );
};

export default CrewChangeManagement;
